import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowRight, Sparkles, Loader2, CheckCircle, KeyRound, Handshake, Building2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import PublicFAQSection from "../components/PublicFAQSection";
import EditableText from "../components/EditableText";
import PreviewModeToggle from "../components/PreviewModeToggle";
import PageBlocks from "../components/PageBlocks";
import { useBrand } from "../providers/BootstrapProvider";

const PARTNER_PROVINCES = [
  { code: "BC", name: "British Columbia" },
  { code: "AB", name: "Alberta" },
  { code: "SK", name: "Saskatchewan" },
  { code: "MB", name: "Manitoba" },
  { code: "ON", name: "Ontario" },
  { code: "QC", name: "Quebec" },
  { code: "NB", name: "New Brunswick" },
  { code: "NS", name: "Nova Scotia" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "NL", name: "Newfoundland & Labrador" },
  { code: "YT", name: "Yukon" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
];
const ALL_PARTNER_PROVINCE_CODES = PARTNER_PROVINCES.map((p) => p.code);

const PrelaunchLanding = () => {
  const navigate = useNavigate();
  const brand = useBrand();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Contact form state — separate from waitlist
  const [qEmail, setQEmail] = useState("");
  const [qName, setQName] = useState("");
  const [qMessage, setQMessage] = useState("");
  const [qSubmitting, setQSubmitting] = useState(false);
  const [qSubmitted, setQSubmitted] = useState(false);
  // Partner-interest form state — completely independent
  const [pForm, setPForm] = useState({
    full_name: "",
    company_name: "",
    email: "",
    offering: "",
    provinces_served: [], // empty == not specified, full list == "All of Canada"
    referral_source: "",
  });
  const [pSubmitting, setPSubmitting] = useState(false);
  const [pSubmitted, setPSubmitted] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/waitlist/subscribe`, {
        email: email.trim(),
        name: name.trim() || null,
        referral_source: "prelaunch_landing",
      });
      setSubmitted(true);
      toast.success(r.data.message || "You're on the list.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!qEmail.trim() || !qMessage.trim()) return;
    setQSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/contact`, {
        email: qEmail.trim(),
        name: qName.trim() || null,
        message: qMessage.trim(),
      });
      setQSubmitted(true);
      toast.success(r.data.message || "Thanks — we'll be in touch soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send your question. Please try again.");
    } finally {
      setQSubmitting(false);
    }
  };

  const handlePartnerInterest = async (e) => {
    e.preventDefault();
    if (!pForm.full_name.trim() || !pForm.company_name.trim() || !pForm.email.trim() || !pForm.offering.trim()) return;
    setPSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/partner-interest`, {
        full_name: pForm.full_name.trim(),
        company_name: pForm.company_name.trim(),
        email: pForm.email.trim(),
        offering: pForm.offering.trim(),
        provinces_served: pForm.provinces_served,
        referral_source: pForm.referral_source.trim() || null,
      });
      setPSubmitted(true);
      toast.success(r.data.message || "Thanks — we'll be in touch within 72 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit your interest. Please try again.");
    } finally {
      setPSubmitting(false);
    }
  };

  const togglePartnerProvince = (code) => {
    setPForm((f) => {
      const has = f.provinces_served.includes(code);
      return { ...f, provinces_served: has ? f.provinces_served.filter((c) => c !== code) : [...f.provinces_served, code] };
    });
  };

  const toggleAllPartnerProvinces = () => {
    setPForm((f) => {
      const allSelected = ALL_PARTNER_PROVINCE_CODES.every((c) => f.provinces_served.includes(c));
      return { ...f, provinces_served: allSelected ? [] : [...ALL_PARTNER_PROVINCE_CODES] };
    });
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] relative overflow-hidden" data-testid="prelaunch-landing">
      {/* Founder-managed text injected at top */}
      <PageBlocks pageKey="prelaunch" placement="top" className="relative z-10 max-w-3xl mx-auto px-6 pt-6" />
      {/* Subtle grain + gradient backdrop */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#6B4E8A 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#6B4E8A]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#8DB5A0]/15 rounded-full blur-3xl pointer-events-none" />

      {/* Minimal nav */}
      <nav className="relative z-10 max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <img src={brand.logo_url} alt="DHDH" className="w-11 h-11 rounded-full object-cover shrink-0" />
          <span className="hidden sm:inline font-semibold text-[#6B4E8A] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
            Dental Hygiene Development Hub
          </span>
        </div>
        <Link
          to="/beta"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-[#E5DFEC] hover:border-[#6B4E8A] text-[#6B4E8A] text-sm font-medium rounded-full shadow-sm hover:shadow-md transition-all shrink-0"
          data-testid="beta-access-link"
        >
          <KeyRound className="w-4 h-4" />
          <span className="hidden xs:inline sm:inline">Have a beta code?</span>
          <span className="xs:hidden sm:hidden">Beta code</span>
        </Link>
      </nav>

      {/* Hero */}
      <main className="relative z-10 max-w-3xl mx-auto px-6 pt-12 sm:pt-20 pb-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/80 border border-[#E5DFEC] rounded-full text-xs font-medium text-[#6B4E8A] mb-8 backdrop-blur-sm">
            <Sparkles className="w-3 h-3 text-[#8DB5A0]" />
            <EditableText
              blockKey="prelaunch.eyebrow"
              fallback="Coming soon · Built by RDHs, for RDHs"
              as="span"
            />
          </div>

          <h1
            className="text-3xl sm:text-5xl lg:text-6xl font-light text-[#2A2A2A] mb-6 leading-[1.1] tracking-tight max-w-full"
            style={{ fontFamily: "Outfit, sans-serif", overflowWrap: "break-word", wordBreak: "break-word" }}
          >
            <EditableText
              blockKey="prelaunch.hero_headline_lead"
              fallback="The home for"
              as="span"
            />{" "}
            <EditableText
              blockKey="prelaunch.hero_headline_accent"
              fallback="Canadian Dental Hygienists"
              as="span"
              className="italic font-medium inline-block max-w-full"
              style={{
                background: "linear-gradient(135deg, #6B4E8A 0%, #8DB5A0 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                paddingRight: "0.15em",
              }}
            />
          </h1>

          <EditableText
            blockKey="prelaunch.hero_subhead"
            fallback="DHDH is a private, verified-only community for registered dental hygienists across Canada — discussions, mentorship, real-time wage transparency, CE tracking, mileage & shift tools, and resources tailored to your scope of practice. All on one platform."
            as="p"
            className="text-base sm:text-lg text-[#475569] leading-relaxed mb-10 max-w-xl mx-auto"
            multiline
          />

          {/* Email capture */}
          {!submitted ? (
            <motion.form
              onSubmit={handleSubscribe}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white/90 backdrop-blur-sm border border-[#E5DFEC] rounded-2xl p-4 sm:p-5 max-w-lg mx-auto shadow-sm"
              data-testid="waitlist-form"
            >
              <div className="flex items-center gap-2 mb-3 text-[#6B4E8A]">
                <Mail className="w-4 h-4 text-[#8DB5A0]" />
                <EditableText
                  blockKey="prelaunch.waitlist_title"
                  fallback="Be the first to know when we launch"
                  as="span"
                  className="text-sm font-medium"
                />
              </div>
              <div className="grid sm:grid-cols-[1fr_1fr] gap-2 mb-2">
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting}
                  className="bg-white border-[#E5DFEC]"
                  data-testid="waitlist-name-input"
                />
                <Input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  className="bg-white border-[#E5DFEC]"
                  data-testid="waitlist-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !email.trim()}
                className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-xl py-5 font-medium"
                data-testid="waitlist-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding you...</>
                ) : (
                  <>
                    <EditableText
                      blockKey="prelaunch.waitlist_button"
                      fallback="Join the waitlist"
                      as="span"
                    />
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              <EditableText
                blockKey="prelaunch.waitlist_footnote"
                fallback="We'll only email you when we launch and on major updates. No spam, ever. Powered by Canadian-hosted infrastructure."
                as="p"
                className="text-[11px] text-[#94A3B8] mt-3 leading-relaxed"
                multiline
              />
            </motion.form>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="bg-gradient-to-br from-[#10B981]/10 to-[#10B981]/5 border border-[#10B981]/30 rounded-2xl p-6 max-w-lg mx-auto"
              data-testid="waitlist-success"
            >
              <CheckCircle className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
              <EditableText
                blockKey="prelaunch.waitlist_success_title"
                fallback="You're on the list!"
                as="p"
                className="text-lg font-medium text-[#6B4E8A] mb-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              />
              <EditableText
                blockKey="prelaunch.waitlist_success_body"
                fallback="We'll be in touch the moment we open the doors."
                as="p"
                className="text-sm text-[#475569]"
                multiline
              />
            </motion.div>
          )}
        </motion.div>

        {/* Contact / "Have a question?" card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10 max-w-lg mx-auto"
          data-testid="contact-card"
        >
          {!qSubmitted ? (
            <form
              onSubmit={handleAskQuestion}
              className="bg-white border border-[#E5DFEC] rounded-2xl p-6 shadow-sm text-left"
            >
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-[#6B4E8A]" />
                <EditableText
                  blockKey="prelaunch.contact_title"
                  fallback="Have a question?"
                  as="h2"
                  className="text-base font-semibold text-[#6B4E8A]"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                />
              </div>
              <EditableText
                blockKey="prelaunch.contact_body"
                fallback="Anything you'd like to know before launch — pricing, features, partnerships, beta access — drop us a note."
                as="p"
                className="text-xs text-[#64748B] mb-4 leading-relaxed"
                multiline
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={qName}
                  onChange={(e) => setQName(e.target.value)}
                  disabled={qSubmitting}
                  className="bg-[#FAF7F2] border-[#E5DFEC] rounded-xl text-sm"
                  data-testid="contact-name-input"
                />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  required
                  value={qEmail}
                  onChange={(e) => setQEmail(e.target.value)}
                  disabled={qSubmitting}
                  className="bg-[#FAF7F2] border-[#E5DFEC] rounded-xl text-sm"
                  data-testid="contact-email-input"
                />
              </div>
              <textarea
                required
                rows={4}
                placeholder="What's your question?"
                value={qMessage}
                onChange={(e) => setQMessage(e.target.value)}
                disabled={qSubmitting}
                maxLength={4000}
                className="w-full bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl text-sm px-3 py-2 placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#6B4E8A]/40 focus:border-[#6B4E8A] resize-none mb-3"
                data-testid="contact-message-input"
              />
              <Button
                type="submit"
                disabled={qSubmitting || !qEmail.trim() || !qMessage.trim()}
                className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] disabled:bg-[#94A3B8] rounded-full"
                data-testid="contact-submit-btn"
              >
                {qSubmitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <>
                    <EditableText
                      blockKey="prelaunch.contact_button"
                      fallback="Send your question"
                      as="span"
                    />
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              <EditableText
                blockKey="prelaunch.contact_footnote"
                fallback="Our team typically responds within 72 hours."
                as="p"
                className="text-[11px] text-[#94A3B8] mt-3 leading-relaxed"
              />
            </form>
          ) : (
            <div
              className="bg-gradient-to-br from-[#10B981]/10 to-[#10B981]/5 border border-[#10B981]/30 rounded-2xl p-6 text-center"
              data-testid="contact-success"
            >
              <CheckCircle className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
              <EditableText
                blockKey="prelaunch.contact_success_title"
                fallback="Question received!"
                as="p"
                className="text-base font-medium text-[#6B4E8A] mb-1"
                style={{ fontFamily: "Outfit, sans-serif" }}
              />
              <EditableText
                blockKey="prelaunch.contact_success_body"
                fallback="Thanks for reaching out — our team typically responds within 72 hours."
                as="p"
                className="text-sm text-[#475569]"
                multiline
              />
            </div>
          )}
        </motion.div>

        {/* What's coming — short list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mt-16 max-w-2xl mx-auto"
        >
          {[
            { key: "prelaunch.feature_pill_1", fallback: "Verified RDH community" },
            { key: "prelaunch.feature_pill_2", fallback: "Anonymous wage data" },
            { key: "prelaunch.feature_pill_3", fallback: "CE & mileage trackers" },
            { key: "prelaunch.feature_pill_4", fallback: "Mentorship matching" },
            { key: "prelaunch.feature_pill_5", fallback: "Province-wide meetups" },
            { key: "prelaunch.feature_pill_6", fallback: "Shift & finance tracking" },
          ].map((feature, i) => (
            <EditableText
              key={feature.key}
              blockKey={feature.key}
              fallback={feature.fallback}
              as="div"
              className="bg-white/60 backdrop-blur-sm border border-[#E5DFEC]/60 rounded-xl px-4 py-3 text-xs sm:text-sm text-[#475569] text-left"
            />
          ))}
        </motion.div>
      </main>

      {/* Founder-managed text injected mid-page (above Partner section) */}
      <PageBlocks pageKey="prelaunch" placement="middle" className="relative z-10 max-w-3xl mx-auto px-6 mt-12" />

      {/* Partner With DHDH — visually distinct cream-on-plum-tinted band */}
      <section
        className="relative z-10 mt-20 border-t border-b border-[#E5DFEC] bg-gradient-to-br from-[#F0ECF7] via-[#FAF7F2] to-[#F0ECF7]"
        data-testid="partner-interest-section"
      >
        <div className="max-w-3xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6B4E8A]/10 border border-[#6B4E8A]/20 text-[#6B4E8A] text-xs font-medium mb-5">
              <Handshake className="w-3.5 h-3.5" /> For Brands & Partners
            </div>
            <EditableText
              blockKey="prelaunch.partner_section_heading"
              fallback="Partner With DHDH"
              as="h2"
              className="text-3xl sm:text-4xl font-medium text-[#6B4E8A] mb-3"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="partner-section-heading"
            />
            <EditableText
              blockKey="prelaunch.partner_section_subtitle"
              fallback="Reach Canada's most engaged dental hygiene community. We offer limited partner opportunities for businesses that serve dental hygiene professionals."
              as="p"
              className="text-sm sm:text-base text-[#475569] leading-relaxed max-w-2xl mx-auto"
              multiline
            />
          </motion.div>

          {!pSubmitted ? (
            <motion.form
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              onSubmit={handlePartnerInterest}
              className="bg-white border border-[#E5DFEC] rounded-3xl p-6 sm:p-8 shadow-sm space-y-4"
              data-testid="partner-interest-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[#6B4E8A] mb-1.5 block">Full name</label>
                  <Input
                    type="text"
                    placeholder="Jordan Lee"
                    value={pForm.full_name}
                    onChange={(e) => setPForm((f) => ({ ...f, full_name: e.target.value }))}
                    required
                    maxLength={120}
                    className="bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="partner-fullname-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6B4E8A] mb-1.5 block">Company name</label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                    <Input
                      type="text"
                      placeholder="Your company"
                      value={pForm.company_name}
                      onChange={(e) => setPForm((f) => ({ ...f, company_name: e.target.value }))}
                      required
                      maxLength={120}
                      className="pl-9 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                      data-testid="partner-company-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-[#6B4E8A] mb-1.5 block">Email address</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={pForm.email}
                    onChange={(e) => setPForm((f) => ({ ...f, email: e.target.value }))}
                    required
                    className="pl-9 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="partner-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-[#6B4E8A] mb-1.5 block">
                  What products or services do you offer?
                </label>
                <textarea
                  value={pForm.offering}
                  onChange={(e) => setPForm((f) => ({ ...f, offering: e.target.value }))}
                  placeholder="Briefly describe what you'd like to offer DHDH members…"
                  required
                  minLength={10}
                  maxLength={4000}
                  rows={4}
                  className="w-full px-3 py-2 rounded-md bg-white border border-[#E5DFEC] focus:border-[#6B4E8A] focus:outline-none text-sm text-[#2A2A2A] placeholder:text-[#94A3B8]"
                  data-testid="partner-offering-input"
                />
              </div>

              <div data-testid="partner-provinces-block">
                <label className="text-xs font-medium text-[#6B4E8A] block">
                  Which provinces or territories does your business serve?
                </label>
                <p className="text-[11px] text-[#94A3B8] mb-2">Select all that apply</p>
                {(() => {
                  const allSelected = ALL_PARTNER_PROVINCE_CODES.every((c) => pForm.provinces_served.includes(c));
                  return (
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={toggleAllPartnerProvinces}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          allSelected
                            ? "bg-[#6B4E8A] text-white border-[#6B4E8A]"
                            : "bg-white text-[#6B4E8A] border-[#E5DFEC] hover:border-[#6B4E8A]"
                        }`}
                        aria-pressed={allSelected}
                        data-testid="partner-province-all"
                      >
                        All of Canada
                      </button>
                      {PARTNER_PROVINCES.map((p) => {
                        const active = pForm.provinces_served.includes(p.code);
                        return (
                          <button
                            key={p.code}
                            type="button"
                            onClick={() => togglePartnerProvince(p.code)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                              active
                                ? "bg-[#8DB5A0] text-white border-[#8DB5A0]"
                                : "bg-white text-[#475569] border-[#E5DFEC] hover:border-[#8DB5A0] hover:text-[#6B4E8A]"
                            }`}
                            aria-pressed={active}
                            data-testid={`partner-province-${p.code}`}
                          >
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              <div>
                <label className="text-xs font-medium text-[#6B4E8A] mb-1.5 block">
                  How did you hear about DHDH? <span className="text-[#94A3B8] font-normal">(optional)</span>
                </label>
                <Input
                  type="text"
                  placeholder="Conference, social media, a colleague, etc."
                  value={pForm.referral_source}
                  onChange={(e) => setPForm((f) => ({ ...f, referral_source: e.target.value }))}
                  maxLength={200}
                  className="bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                  data-testid="partner-referral-input"
                />
              </div>

              <Button
                type="submit"
                disabled={
                  pSubmitting ||
                  !pForm.full_name.trim() ||
                  !pForm.company_name.trim() ||
                  !pForm.email.trim() ||
                  pForm.offering.trim().length < 10
                }
                className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full py-5 font-medium"
                data-testid="partner-submit-btn"
              >
                {pSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <EditableText
                      blockKey="prelaunch.partner_button"
                      fallback="Express Interest"
                      as="span"
                    /> <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>

              <EditableText
                blockKey="prelaunch.partner_spots_note"
                fallback="Partner spots are limited. Our team will be in touch within 72 hours."
                as="p"
                className="text-[11px] text-[#94A3B8] text-center mt-1 leading-relaxed"
                data-testid="partner-spots-note"
                multiline
              />
            </motion.form>
          ) : (
            <div
              className="bg-gradient-to-br from-[#10B981]/10 to-[#10B981]/5 border border-[#10B981]/30 rounded-2xl p-6 text-center"
              data-testid="partner-interest-success"
            >
              <CheckCircle className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
              <EditableText
                blockKey="prelaunch.partner_success_title"
                fallback="Thanks for reaching out!"
                as="p"
                className="text-base font-medium text-[#6B4E8A] mb-1"
                style={{ fontFamily: "Outfit, sans-serif" }}
              />
              <EditableText
                blockKey="prelaunch.partner_success_body"
                fallback="We're excited to connect with you and will be in touch within 72 hours."
                as="p"
                className="text-sm text-[#475569]"
                multiline
              />
            </div>
          )}
        </div>
      </section>

      {/* FAQ — fully editable by founder via /admin/faq */}
      <PublicFAQSection page="prelaunch" />

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#E5DFEC] bg-white/40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col gap-3 text-xs text-[#94A3B8]">
          <EditableText
            blockKey="prelaunch.footer_disclaimer"
            fallback="DHDH is a peer community platform. Content shared by members does not constitute clinical, legal, or financial advice. Always consult a licensed professional."
            as="p"
            className="leading-relaxed text-center sm:text-left max-w-3xl"
            data-testid="global-platform-disclaimer"
            multiline
          />
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p>© {new Date().getFullYear()} Dental Hygiene Development Hub · Built in Alberta, Canada</p>
            <div className="flex gap-5 flex-wrap">
              <a href="/privacy" className="hover:text-[#6B4E8A] transition-colors" data-testid="footer-link-privacy">Privacy</a>
              <a href="/terms" className="hover:text-[#6B4E8A] transition-colors" data-testid="footer-link-terms">Terms</a>
              <a href="/cookie-policy" className="hover:text-[#6B4E8A] transition-colors" data-testid="footer-link-cookie">Cookie Policy</a>
              <a href="/disclaimer" className="hover:text-[#6B4E8A] transition-colors" data-testid="footer-link-disclaimer">Disclaimer</a>
              <a href="mailto:support@dhdh.ca" className="hover:text-[#6B4E8A] transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Founder-managed text at bottom of prelaunch page */}
      <PageBlocks pageKey="prelaunch" placement="bottom" className="relative z-10 max-w-3xl mx-auto px-6 pb-10" />

      {/* Founder preview-mode toggle (renders only for founder/master-admin) */}
      <PreviewModeToggle />
    </div>
  );
};

export default PrelaunchLanding;
